import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { ApiError } from '@web/core/http/api-error';
import { authApi, type Me } from './auth-api';
import { ME_QUERY_KEY, meQueryOptions } from './session';
import { safeNextPath } from './safe-next-path';
import { apiRuntime } from '@web/core/http/api-config';

/**
 * The screen of a user who still holds a temporary password. The API refuses everything
 * else (`403 PASSWORD_CHANGE_REQUIRED`) until the password is changed; the web sends the
 * user here instead of drawing the app.
 */
export const CHANGE_PASSWORD_PATH = '/change-password';

/** Where `?next=` should point while the change is pending (`undefined`: home). */
function nextSearch(href: string): { next?: string } {
  const next = safeNextPath(href, '');
  return next && next !== '/' ? { next } : {};
}

/**
 * Platform operators use the admin app. A `platform-admin` session that lands on the CRM
 * web is signed out and sent to login with a clear reason.
 */
async function rejectPlatformOperator(queryClient: QueryClient): Promise<never> {
  queryClient.setQueryData(ME_QUERY_KEY, undefined);
  queryClient.removeQueries({ queryKey: ['auth'] });
  await authApi.logout().catch(() => undefined);
  throw redirect({
    to: '/login',
    search: { reason: 'platform_operator' },
    replace: true,
  });
}

/**
 * `beforeLoad` of the protected `_app` layout: ensures the session (none → `/login`), and
 * sends a user with a pending password change to `/change-password`, coming back afterwards.
 * Platform-admin memberships are refused here (they belong in the admin app). An ended demo
 * stays in the layout, which shows only the "demo ended" notice (the API would refuse the
 * password change too).
 */
export async function requireSettledSession(queryClient: QueryClient, href: string): Promise<Me> {
  let me: Me;
  try {
    me = await queryClient.ensureQueryData(meQueryOptions);
  } catch (error) {
    // Embedded, the host signs people in (the API client already told it): no CRM login.
    if (error instanceof ApiError && error.status === 401 && !apiRuntime().embedded) {
      throw redirect({ to: '/login', search: { next: href }, replace: true });
    }
    throw error;
  }
  if (me.membership.role === 'platform-admin') {
    await rejectPlatformOperator(queryClient);
  }
  if (me.user.mustChangePassword && !me.demo?.expired) {
    throw redirect({ to: CHANGE_PASSWORD_PATH, search: nextSearch(href), replace: true });
  }
  return me;
}

/**
 * `beforeLoad` of `/change-password`: only for a session with a pending change. Without a
 * session → `/login`; with the password already settled → `next` (or home).
 */
export async function requirePendingPasswordChange(
  queryClient: QueryClient,
  next: string | undefined,
): Promise<Me> {
  const me = await queryClient.ensureQueryData(meQueryOptions).catch((error: unknown) => {
    if (error instanceof ApiError && error.status === 401) {
      throw redirect({ to: '/login', replace: true });
    }
    throw error;
  });
  if (!me.user.mustChangePassword) throw redirect({ href: safeNextPath(next), replace: true });
  return me;
}

/**
 * Where a fresh sign-in goes: `next` (or home); with a temporary password, straight to
 * `/change-password` — the shell is never drawn first — keeping `next` for afterwards.
 */
export function destinationAfterSignIn(
  user: { mustChangePassword: boolean },
  next: string | undefined,
): string {
  const destination = safeNextPath(next);
  if (!user.mustChangePassword) return destination;
  return destination === '/'
    ? CHANGE_PASSWORD_PATH
    : `${CHANGE_PASSWORD_PATH}?${new URLSearchParams({ next: destination }).toString()}`;
}

/**
 * The API said the password must be changed: the cached session agrees from now on (so the
 * guards above do not bounce between the screens), and the user goes to the change screen.
 * Already there, nothing happens — no loop.
 */
export function handlePasswordChangeRequired(
  queryClient: QueryClient,
  location: { pathname: string; href: string },
  navigate: (to: { to: typeof CHANGE_PASSWORD_PATH; search: { next?: string } }) => void,
): void {
  queryClient.setQueryData<Me>(ME_QUERY_KEY, (me) =>
    me ? { ...me, user: { ...me.user, mustChangePassword: true } } : me,
  );
  if (location.pathname === CHANGE_PASSWORD_PATH) return;
  navigate({ to: CHANGE_PASSWORD_PATH, search: nextSearch(location.href) });
}
